import React, { ComponentType, ErrorInfo } from 'react';

import { connect } from 'react-redux';

import { ErrorPageType } from '../../components/error-page/types';
import { setErrorPageAction } from '../../features/config';
import { createLogger } from '../loggerFacade';

const logger = createLogger('ErrorBoundary');

interface ErrorBoundaryProps {
    setErrorPage: typeof setErrorPageAction;
    componentName?: string;
}

class ErrorBoundary extends React.PureComponent<React.PropsWithChildren<ErrorBoundaryProps>> {
    componentDidCatch(error: Error, info: ErrorInfo) {
        logger.error(error, {
            block: 'ErrorBoundary',
            level: 'critical',
            method: 'componentDidCatch',
            type: 'unknown error',
            additional: {
                ...info,
                componentName: this.props.componentName,
            },
        });

        this.props.setErrorPage({
            type: ErrorPageType.DEFAULT,
            reason: {
                method: 'withErrorBoundary',
                message: error.message,
                type: 'unknown error',
                ...info,
                componentName: this.props.componentName,
            },
        });
    }

    render() {
        return this.props.children;
    }
}

const ConnectedErrorBoundary = connect(null, {
    setErrorPage: setErrorPageAction,
})(ErrorBoundary);

/**
 *
 * @param {React.Component} DecoratedComponent
 * @returns {React.Component}
 */
export function withErrorBoundary<BaseProps extends object>(DecoratedComponent: ComponentType<BaseProps>) {
    return class WithErrorBoundary extends React.Component<BaseProps> {
        static displayName = `WithErrorBoundary(${DecoratedComponent.displayName})`;

        render() {
            return (
                <ConnectedErrorBoundary componentName={DecoratedComponent.displayName}>
                    <DecoratedComponent {...(this.props as BaseProps)} />
                </ConnectedErrorBoundary>
            );
        }
    };
}
